/** Competition bulk — take N owner-direct listings off a set of competitors.
 *
 *  The flow this page is built around (and why it isn't just the
 *  ``BulkOwnerFinderModal`` again):
 *
 *    1. Pick a handful of competitor firms — not one at a time, and not the
 *       whole market. The scan unions them, so "these four agencies" is a
 *       single sweep.
 *    2. Say how many listings you actually want to publish. The scan keeps
 *       collecting past that number so there's a reserve to draw on.
 *    3. Before a single publish job is spawned, every picked listing is
 *       verified. The two scrapers publish their own takedown signals, so
 *       most verdicts come back from one indexed query and only the rows
 *       nobody has re-fetched recently cost a portal round-trip. Dead ones
 *       are dropped and **replaced** from the reserve (scanning deeper when
 *       the reserve runs out), so the batch that finally spawns is N *live*
 *       listings, not N optimistic ones.
 *
 *  The whole page reads ``market_listings`` — the live union of
 *  ``ssparser.listings`` and ``mhparser.listings``, seconds old. It used to
 *  read ``hot_offers``, whose ingest feed was decommissioned in July 2026.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ApiError, api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/Skeleton";
import { usePublishProgress } from "@/components/PublishProgress";
import {
  FilterBar,
  TimeRangeDropdown,
  filtersToBody,
  hasAnyFilter,
  resolveTimeRange,
  type Filters,
  type MarketListing,
  type TimeRangeValue,
} from "@/components/MarketShared";
import type { TeamMember } from "@/types";

interface AgencyRow {
  /** Portal-native firm key — ``ss:<companyId>``, ``ssu:<userId>`` or
   *  ``mh:<sellerId>``. Opaque to this page; it is the identity used for
   *  selection, counts and scan scope alike. */
  id: string;
  platform: string;
  /** The portal's own id without the namespace prefix. */
  external_id: string;
  name: string | null;
  logo_url: string | null;
  color_hex: string | null;
  /** The portal's classification: agency / company / broker / agent /
   *  developer. */
  user_type: string | null;
  /** The firm an ss agent works under — disambiguates two brokers who share
   *  a first name. */
  parent_name: string | null;
  /** The portal directory's own listing count. Only MyHome publishes one,
   *  and it reads 0 for a number of genuinely large firms, so it's a
   *  fallback only — never the size we rank or lead with. */
  portal_listings: number | null;
  /** Active listings we currently hold for this firm, refreshed every few
   *  minutes in the background. What the picker ranks by, and the size shown
   *  until the exact count for a picked firm arrives. */
  active_listings: number | null;
}

/** Exact counts for a picked firm — what a scan of it will really walk.
 *  Fetched separately and only for picked firms: the browse list is ranked
 *  from a cached rollup, which is right for ordering but is up to a few
 *  minutes behind. */
interface AgencyCounts {
  id: string;
  active_listings: number;
  recent_7d: number;
  recent_30d: number;
}

interface OwnerCandidate {
  score: number;
  method: "cadastral" | "fingerprint_tight" | "fingerprint" | "self" | string;
  reasons: string[];
  listing: MarketListing;
}

interface FinderItem {
  competitor_listing: MarketListing;
  top_candidate: OwnerCandidate | null;
  candidate_count: number;
}

interface ScanResponse {
  items: FinderItem[];
  total: number;
  /** Keyset cursor for the next page — see ``cursor`` below. */
  next_cursor?: string | null;
  /** ``total`` is a floor, not the real count — the server stops counting at
   *  a ceiling rather than counting every row of a 12 GB table. */
  total_capped?: boolean;
  /** False when the server gave up counting inside its budget — treat the
   *  total as unknown and sweep until a short page. */
  total_exact?: boolean;
  processed: number;
  matched: number;
}

type Verdict = "live" | "gone" | "unknown";

/** Result-list buckets. ``all`` still hides nothing; the rest are the
 *  questions people actually ask of a finished scan ("what's left to do?",
 *  "what did we already take?", "what died in verification?"). */
type MatchView =
  | "all"
  | "actionable"
  | "picked"
  | "uploaded"
  | "deleted"
  | "unmatched";

const VIEW_LABELS: { value: MatchView; label: string }[] = [
  { value: "all", label: "All" },
  { value: "actionable", label: "Actionable" },
  { value: "picked", label: "Picked" },
  { value: "uploaded", label: "Already ours" },
  { value: "deleted", label: "Deleted" },
  { value: "unmatched", label: "No owner" },
];

/** How the owner's listing was identified. Ordered strongest first — see
 *  ``app/services/market/live_owner_finder.py`` for what each one proves. */
const METHOD_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Any confidence" },
  { value: "cadastral", label: "Cadastral code (certain)" },
  { value: "fingerprint_tight", label: "Tight match (high)" },
  { value: "fingerprint", label: "Unique match (medium)" },
  { value: "self", label: "Already owner-direct" },
];

/** Methods that identify the property outright rather than by resemblance. */
const CERTAIN_METHODS = new Set(["cadastral", "self"]);

/** Compact labels for the result badge — the raw method names are for the
 *  filter dropdown, where there is room to explain them. */
const METHOD_LABELS: Record<string, string> = {
  cadastral: "cadastral",
  fingerprint_tight: "tight",
  fingerprint: "unique",
  self: "owner",
};

const SCORE_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "Any score" },
  { value: 60, label: "Score ≥ 60" },
  { value: 80, label: "Score ≥ 80" },
  { value: 90, label: "Score ≥ 90" },
];

interface PreflightResponse {
  /** ``source`` is ``db`` when the scrapers already knew the answer and
   *  ``portal`` when we had to ask the site. */
  items: { id: string; status: Verdict; source?: "db" | "portal" }[];
  live: number;
  gone: number;
  unknown: number;
}

interface PublishResponse {
  jobs: { job_run_id: string }[];
  skipped_offer_ids: string[];
  /** Rows the server itself refused as off the portal (a verdict recorded
   *  since the preflight ran, or the parser dropped the row). */
  gone_offer_ids?: string[];
  /** "Owner" rows the server unmasked as agencies registered as private
   *  sellers — never published. */
  agent_offer_ids?: string[];
  /** Rows without photos — MyHome rejects an image-less listing, so the
   *  server skips them instead of spawning a job that can only fail. */
  no_photo_offer_ids?: string[];
  batch_id: string;
}

// Rows per scan round-trip. Each row costs the server a dedup + heuristic
// lookup, so small pages keep every call sub-second and let matches stream in.
const SCAN_PAGE = 25;
// Listings per liveness call. The parsers never re-fetch a listing after
// ingest, so anything older than an hour is asked of the portal itself —
// ~1s per ss.ge row, ~2s per MyHome row through a headless browser (the
// MyHome API has been locked since 2026-10-07). A chunk of 10 keeps each
// round-trip to a few seconds so the verified counter visibly advances.
const PREFLIGHT_CHUNK = 10;
// Committed-to-state cadence for streamed scan pages — see the same trick in
// BulkOwnerFinderModal: without it a long sweep re-renders the list per page.
const FLUSH_MS = 200;

// How much spare inventory to gather on top of the target. Verification
// still finds some of the picked listings already taken down, so the scan
// collects this multiple of the target and keeps the remainder as the
// reserve replacements are drawn from. If even that runs dry mid-verification
// the loop just scans deeper — nobody has to pre-guess a depth.
const RESERVE_MULTIPLIER = 2;
// Safety ceiling on rows walked in one scan, so "publish 500" against a firm
// with no owner-matchable inventory can't sweep forever. Verification can
// still page past it when it needs replacements.
const MAX_SCAN_ROWS = 20000;

export function CompetitionBulk() {
  // ── step 1: competitors ──────────────────────────────────────────
  const [agencyQuery, setAgencyQuery] = useState("");
  const [agencyOptions, setAgencyOptions] = useState<AgencyRow[] | null>(null);
  const [agencyError, setAgencyError] = useState<string | null>(null);
  const [picked, setPicked] = useState<AgencyRow[]>([]);
  // Exact rollup counts, keyed by agency id — filled in for picked firms
  // shortly after they're picked. Absent while in flight, and stays absent if
  // the server gave up on the count (it self-limits); the chip then falls
  // back to the portal's own number rather than showing an error.
  const [pickedCounts, setPickedCounts] = useState<Map<string, AgencyCounts>>(
    new Map(),
  );

  // ── step 2: scan constraints ─────────────────────────────────────
  const [filters, setFilters] = useState<Filters>({});
  // Open by default: these decide what gets scanned at all, so hiding them
  // behind a toggle just means people scan the wrong slice first.
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRangeValue>({ mode: "all" });
  const [target, setTarget] = useState(50);
  // Unpublished owner matches banked by the current scan — the number the
  // progress bar fills against (target + reserve), not rows walked.
  const [collected, setCollected] = useState(0);

  const [items, setItems] = useState<FinderItem[] | null>(null);
  const [scanned, setScanned] = useState(0);
  const [scanTotal, setScanTotal] = useState(0);
  // ``scanTotal`` is a floor when set — the server stops counting at a ceiling.
  const [scanTotalCapped, setScanTotalCapped] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);

  // ── step 3: agents + publish ─────────────────────────────────────
  const [team, setTeam] = useState<TeamMember[] | null>(null);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [useSs, setUseSs] = useState(true);
  const [mhPayBalance, setMhPayBalance] = useState(true);
  // Also require the *competitor's* posting to still be live. On by default:
  // if they pulled their ad the property is usually off the market, and
  // republishing the owner's twin is wasted work.
  const [strictCompetitor, setStrictCompetitor] = useState(true);

  const [verdicts, setVerdicts] = useState<Map<string, Verdict>>(new Map());
  const [verifyState, setVerifyState] = useState<{
    checked: number;
    dead: number;
    replaced: number;
    /** "unknown" verdicts swapped for a verified replacement. */
    unverified: number;
    target: number;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  // ── results filters ──────────────────────────────────────────────
  // These narrow what's *shown* (and what "Pick top N" picks from) without
  // re-running the scan — the property filters above do that. Selection is
  // deliberately unaffected: a row you picked stays queued for publish even
  // if you then filter it out of view.
  const [view, setView] = useState<MatchView>("all");
  const [agencyFilter, setAgencyFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [minScore, setMinScore] = useState(0);
  const [rowQuery, setRowQuery] = useState("");

  const { registerBatch } = usePublishProgress();

  // Paging cursor for the competitor sweep. Shared by the initial scan and
  // by the top-up scans the verification step triggers when the reserve of
  // spare matches runs dry — both walk the same ordered result set, so a
  // replacement is always "the next listing we hadn't looked at yet".
  const cursor = useRef({
    offset: 0,
    /** Server-issued keyset cursor. Preferred over ``offset`` when present:
     *  a sweep can walk 20k rows, and offset paging makes the server re-read
     *  and re-sort every earlier row on every page — once per picked firm,
     *  since the scan fans out per firm. Null on the first page and once the
     *  sort in use can't be paged by keyset. */
    keyset: null as string | null,
    exhausted: false,
    total: 0,
    capped: false,
  });
  const abortRef = useRef<AbortController | null>(null);

  const pickedIds = useMemo(() => picked.map((a) => a.id), [picked]);

  const scanBody = useMemo(
    () => ({
      // Portal-native firm keys ("ss:2263" / "ssu:<uuid>" / "mh:4088126").
      // Replaces ``market_agency_ids``, which pointed into the scraped
      // ``market_agencies`` table the picker no longer reads.
      firm_keys: pickedIds,
      sort: filters.sort || "newest",
      ...filtersToBody(filters),
      ...resolveTimeRange(timeRange),
    }),
    [pickedIds, filters, timeRange],
  );
  const scanKey = useMemo(() => JSON.stringify(scanBody), [scanBody]);

  // ── competitor picker ────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const q = agencyQuery.trim();
    const handle = window.setTimeout(
      () => {
        api<{ query: string; items: AgencyRow[] }>(
          `/api/competition-bulk/agencies?limit=30${q ? `&q=${encodeURIComponent(q)}` : ""}`,
        )
          .then((r) => {
            if (cancelled) return;
            setAgencyOptions(r.items);
            setAgencyError(null);
          })
          .catch((e) => {
            if (cancelled) return;
            setAgencyError(
              e instanceof ApiError ? e.message : "Failed to load competitors",
            );
          });
      },
      q ? 250 : 0,
    );
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [agencyQuery]);

  useEffect(() => {
    let cancelled = false;
    api<TeamMember[]>("/api/team")
      .then((r) => {
        if (cancelled) return;
        // Owners don't publish inventory themselves — same exclusion the
        // other bulk flows apply.
        setTeam(
          r.filter(
            (m) => m.status === "active" && m.role !== "executive_manager",
          ),
        );
      })
      .catch((e) => {
        if (cancelled) return;
        setTeamError(e instanceof Error ? e.message : "Failed to load team");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Resolve exact counts for whatever is picked. Debounced so rapid-fire
  // picking issues one request, and failure-tolerant: a missing count just
  // leaves the portal figure on the chip.
  useEffect(() => {
    const ids = picked.map((a) => a.id).filter((id) => !pickedCounts.has(id));
    if (ids.length === 0) return;
    let cancelled = false;
    const handle = window.setTimeout(() => {
      api<{ items: AgencyCounts[] }>("/api/competition-bulk/agencies/counts", {
        method: "POST",
        json: { firm_keys: ids.slice(0, 25) },
      })
        .then((r) => {
          if (cancelled) return;
          setPickedCounts((prev) => {
            const next = new Map(prev);
            for (const c of r.items) next.set(c.id, c);
            return next;
          });
        })
        .catch(() => {
          /* decorative — keep the portal number */
        });
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [picked, pickedCounts]);

  function togglePicked(a: AgencyRow) {
    setPicked((prev) =>
      prev.some((p) => p.id === a.id)
        ? prev.filter((p) => p.id !== a.id)
        : [...prev, a],
    );
  }

  // Any change to what would be scanned invalidates the results — the user
  // re-runs the scan rather than acting on rows the constraints no longer
  // describe.
  const resetScan = useCallback(() => {
    abortRef.current?.abort();
    cursor.current = {
      offset: 0,
      keyset: null,
      exhausted: false,
      total: 0,
      capped: false,
    };
    setItems(null);
    setScanned(0);
    setScanTotal(0);
    setScanTotalCapped(false);
    setCollected(0);
    setScanning(false);
    setSelected(new Set());
    setVerdicts(new Map());
    setVerifyState(null);
    setResult(null);
    setSubmitError(null);
  }, []);

  useEffect(() => {
    resetScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanKey]);

  // ── scan machinery ───────────────────────────────────────────────

  /** One page of the competitor sweep, advancing the shared cursor.
   *  Returns null once the filtered inventory is exhausted. */
  const fetchPage = useCallback(
    async (signal?: AbortSignal): Promise<FinderItem[] | null> => {
      if (cursor.current.exhausted) return null;
      const r = await api<ScanResponse>("/api/competition-bulk/scan", {
        method: "POST",
        json: {
          ...scanBody,
          limit: SCAN_PAGE,
          offset: cursor.current.offset,
          cursor: cursor.current.keyset,
        },
        signal,
      });
      cursor.current.total = r.total;
      cursor.current.capped = !!r.total_capped;
      cursor.current.offset += r.processed;
      cursor.current.keyset = r.next_cursor ?? null;
      // A short page is the authoritative "that's everything" signal. The
      // total is only a stop condition when the server counted exactly —
      // when it's capped, there's more inventory past it.
      if (
        r.processed < SCAN_PAGE ||
        (r.total_exact !== false &&
          !r.total_capped &&
          cursor.current.offset >= r.total)
      ) {
        cursor.current.exhausted = true;
      }
      setScanTotal(r.total);
      setScanTotalCapped(!!r.total_capped);
      setScanned(cursor.current.offset);
      return r.items;
    },
    [scanBody],
  );

  async function startScan() {
    if (pickedIds.length === 0) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    cursor.current = {
      offset: 0,
      keyset: null,
      exhausted: false,
      total: 0,
      capped: false,
    };
    setItems([]);
    setScanned(0);
    setCollected(0);
    setSelected(new Set());
    setVerdicts(new Map());
    setVerifyState(null);
    setResult(null);
    setScanError(null);
    setScanning(true);

    // Buffer arriving rows + auto-selections and commit them on a throttle,
    // so a multi-thousand-row sweep doesn't re-render the list per page.
    let buffer: FinderItem[] = [];
    let selectBuffer: string[] = [];
    let lastFlush = 0;
    // Mirrors ``selected`` during the sweep — reading state here would see a
    // stale snapshot inside the loop.
    let autoSelected = 0;
    const seenCandidates = new Set<string>();

    const flush = () => {
      if (buffer.length) {
        const b = buffer;
        buffer = [];
        setItems((prev) => (prev ? prev.concat(b) : b.slice()));
      }
      if (selectBuffer.length) {
        const s = selectBuffer;
        selectBuffer = [];
        setSelected((prev) => {
          const next = new Set(prev);
          for (const id of s) next.add(id);
          return next;
        });
      }
    };

    // Walk newest-first until we've banked the target plus its reserve. No
    // depth to choose: how far we go is whatever it takes to find that many
    // publishable owner listings (bounded by MAX_SCAN_ROWS / their inventory).
    const want = target * RESERVE_MULTIPLIER;
    try {
      while (!controller.signal.aborted) {
        if (seenCandidates.size >= want) break;
        if (cursor.current.offset >= MAX_SCAN_ROWS) break;
        const page = await fetchPage(controller.signal);
        if (page === null) break;
        for (const it of page) {
          buffer.push(it);
          const cand = it.top_candidate?.listing;
          if (!cand || cand.placement_id) continue;
          if (seenCandidates.has(cand.id)) continue;
          seenCandidates.add(cand.id);
          // Pre-select up to the publish target; everything past it stays in
          // the list as the reserve the verification step draws from.
          if (autoSelected < target) {
            selectBuffer.push(cand.id);
            autoSelected += 1;
          }
        }
        setCollected(seenCandidates.size);
        const now = performance.now();
        if (now - lastFlush >= FLUSH_MS) {
          lastFlush = now;
          flush();
        }
      }
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) {
        setScanError(
          e instanceof ApiError ? e.message : "Scan failed — try again",
        );
      }
    } finally {
      flush();
      if (!controller.signal.aborted) setScanning(false);
      if (abortRef.current === controller) abortRef.current = null;
    }
  }

  function stopScan() {
    abortRef.current?.abort();
    setScanning(false);
  }

  // ── derived views ────────────────────────────────────────────────

  /** Matches first (confidence desc, already-published sunk to the bottom),
   *  then unmatched rows. De-duplicated on the owner listing: several
   *  competitor posts routinely resolve to the same owner ad, and queueing
   *  it twice would publish the same flat twice. */
  const displayItems = useMemo(() => {
    const list = items ?? [];
    const matched = list.filter((it) => it.top_candidate);
    const unmatched = list.filter((it) => !it.top_candidate);
    matched.sort((a, b) => {
      const ap = a.top_candidate!.listing.placement_id ? 1 : 0;
      const bp = b.top_candidate!.listing.placement_id ? 1 : 0;
      if (ap !== bp) return ap - bp;
      return (b.top_candidate?.score ?? 0) - (a.top_candidate?.score ?? 0);
    });
    const seen = new Set<string>();
    const deduped: FinderItem[] = [];
    for (const it of matched) {
      const cid = it.top_candidate!.listing.id;
      if (seen.has(cid)) continue;
      seen.add(cid);
      deduped.push(it);
    }
    return [...deduped, ...unmatched];
  }, [items]);

  /** Owner-listing id → the competitor row it was found through. Drives the
   *  strict-mode check (is their ad still up?) and the per-row rendering. */
  const byCandidate = useMemo(() => {
    const m = new Map<string, FinderItem>();
    for (const it of displayItems) {
      const cid = it.top_candidate?.listing.id;
      if (cid && !m.has(cid)) m.set(cid, it);
    }
    return m;
  }, [displayItems]);

  // The verify loop runs across awaits, where the memos above are frozen at
  // the render that started it — including for rows fetched *during* the
  // loop. This ref is the loop's live view of both.
  const viewRef = useRef<{
    items: FinderItem[];
    byCandidate: Map<string, FinderItem>;
  }>({ items: [], byCandidate: new Map() });
  useEffect(() => {
    viewRef.current = { items: displayItems, byCandidate };
  }, [displayItems, byCandidate]);

  const orderedSelected = useMemo(
    () =>
      displayItems
        .map((it) => it.top_candidate?.listing.id)
        .filter((id): id is string => !!id && selected.has(id)),
    [displayItems, selected],
  );

  /** Which bucket a row falls in — one place, so the chip counts and the
   *  filtering can never disagree. */
  const bucketOf = useCallback(
    (it: FinderItem): Exclude<MatchView, "all"> => {
      const cand = it.top_candidate?.listing;
      if (!cand) return "unmatched";
      if (
        verdicts.get(cand.id) === "gone" ||
        verdicts.get(it.competitor_listing.id) === "gone"
      )
        return "deleted";
      if (cand.placement_id) return "uploaded";
      if (selected.has(cand.id)) return "picked";
      return "actionable";
    },
    [verdicts, selected],
  );

  const viewCounts = useMemo(() => {
    const c: Record<MatchView, number> = {
      all: displayItems.length,
      actionable: 0,
      picked: 0,
      uploaded: 0,
      deleted: 0,
      unmatched: 0,
    };
    for (const it of displayItems) c[bucketOf(it)] += 1;
    return c;
  }, [displayItems, bucketOf]);

  /** What the list actually renders: the bucket filter, plus the per-firm /
   *  confidence / score / text narrowing. */
  const visibleItems = useMemo(() => {
    const needle = rowQuery.trim().toLowerCase();
    return displayItems.filter((it) => {
      if (view !== "all" && bucketOf(it) !== view) return false;
      if (agencyFilter && it.competitor_listing.market_agency_id !== agencyFilter)
        return false;
      const cand = it.top_candidate;
      if (methodFilter && cand?.method !== methodFilter) return false;
      if (minScore > 0 && (cand?.score ?? 0) < minScore) return false;
      if (needle) {
        const hay = [
          it.competitor_listing.title,
          it.competitor_listing.neighborhood,
          it.competitor_listing.district,
          it.competitor_listing.street,
          it.competitor_listing.external_id,
          cand?.listing.title,
          cand?.listing.neighborhood,
          cand?.listing.external_id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [
    displayItems,
    view,
    bucketOf,
    agencyFilter,
    methodFilter,
    minScore,
    rowQuery,
  ]);

  const rowFiltersActive =
    view !== "all" ||
    !!agencyFilter ||
    !!methodFilter ||
    minScore > 0 ||
    !!rowQuery.trim();

  function clearRowFilters() {
    setView("all");
    setAgencyFilter("");
    setMethodFilter("");
    setMinScore(0);
    setRowQuery("");
  }

  const matchedCount = useMemo(
    () => displayItems.filter((it) => it.top_candidate).length,
    [displayItems],
  );
  const reserveCount = useMemo(
    () =>
      displayItems.filter(
        (it) =>
          it.top_candidate &&
          !it.top_candidate.listing.placement_id &&
          !selected.has(it.top_candidate.listing.id) &&
          verdicts.get(it.top_candidate.listing.id) !== "gone",
      ).length,
    [displayItems, selected, verdicts],
  );

  const sortedTeam = useMemo(() => {
    if (!team) return null;
    return [...team].sort(
      (a, b) =>
        (a.placements_today ?? 0) - (b.placements_today ?? 0) ||
        (a.full_name || a.email).localeCompare(b.full_name || b.email),
    );
  }, [team]);

  const orderedAgents = useMemo(
    () => (sortedTeam ?? []).filter((m) => selectedAgents.has(m.id)).map((m) => m.id),
    [sortedTeam, selectedAgents],
  );

  const uncredentialled = useMemo(
    () =>
      (team ?? []).filter(
        (m) =>
          selectedAgents.has(m.id) &&
          (!m.has_myhome_creds || !m.has_myhome_phone),
      ),
    [team, selectedAgents],
  );

  const canPublish =
    orderedSelected.length > 0 &&
    orderedAgents.length > 0 &&
    uncredentialled.length === 0 &&
    !busy &&
    !scanning;

  // ── verify + publish ─────────────────────────────────────────────

  /** The next unused match to stand in for one that turned out deleted.
   *  Draws from the already-scanned reserve first, then keeps scanning
   *  deeper — the whole point of the target/cap split. */
  async function takeReplacement(
    used: Set<string>,
    dead: Set<string>,
    signal: AbortSignal,
  ): Promise<string | null> {
    const fromList = (list: FinderItem[]): string | null => {
      for (const it of list) {
        const cand = it.top_candidate?.listing;
        if (!cand) continue;
        if (cand.placement_id) continue;
        if (used.has(cand.id) || dead.has(cand.id)) continue;
        return cand.id;
      }
      return null;
    };
    const ready = fromList(viewRef.current.items);
    if (ready) return ready;
    // Reserve empty — walk further into the competitors' inventory.
    while (!cursor.current.exhausted && !signal.aborted) {
      const page = await fetchPage(signal);
      if (page === null) break;
      setItems((prev) => (prev ? prev.concat(page) : page.slice()));
      // Keep the loop's own view current — the ``items`` state update above
      // won't have re-rendered (and refreshed the ref) before the next
      // iteration reads it.
      indexPage(viewRef.current, page);
      const hit = fromList(page);
      if (hit) return hit;
    }
    return null;
  }

  async function verifyAndPublish() {
    if (!canPublish) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setBusy(true);
    setSubmitError(null);
    setResult(null);

    const wanted = orderedSelected.length;
    const confirmed: string[] = [];
    const dead = new Set<string>();
    const used = new Set<string>(orderedSelected);
    const queue = [...orderedSelected];
    let replaced = 0;
    let unverified = 0;
    let checked = 0;
    setVerifyState({ checked: 0, dead: 0, replaced: 0, unverified: 0, target: wanted });

    try {
      while (queue.length && confirmed.length < wanted) {
        const chunk = queue.splice(0, PREFLIGHT_CHUNK);
        // Probe the owner listing we'd publish and — in strict mode — the
        // competitor ad we found it through, in one call.
        const ids = new Set<string>(chunk);
        if (strictCompetitor) {
          for (const id of chunk) {
            const src = viewRef.current.byCandidate.get(id)
              ?.competitor_listing.id;
            if (src) ids.add(src);
          }
        }
        const r = await api<PreflightResponse>(
          "/api/competition-bulk/preflight",
          {
            method: "POST",
            json: { listing_ids: [...ids] },
            signal: controller.signal,
          },
        );
        const status = new Map(r.items.map((i) => [i.id, i.status]));
        setVerdicts((prev) => {
          const next = new Map(prev);
          for (const [id, v] of status) next.set(id, v);
          return next;
        });

        for (const id of chunk) {
          checked += 1;
          const srcId = viewRef.current.byCandidate.get(id)
            ?.competitor_listing.id;
          const ownerGone = status.get(id) === "gone";
          const theirsGone =
            strictCompetitor && !!srcId && status.get(srcId) === "gone";
          if (!ownerGone && !theirsGone) {
            if (status.get(id) === "live") {
              confirmed.push(id);
              continue;
            }
            // "unknown" means the portal didn't answer definitively (a half-served
            // page, a throttle, a timeout). Two such rows went to publish on
            // 2026-10-17 and failed as deleted. Swap the row for a verified
            // replacement when there is one; keep it only when there is
            // nothing else, so a probe outage can't empty the batch.
            const rep = await takeReplacement(used, dead, controller.signal);
            if (!rep) {
              confirmed.push(id);
              continue;
            }
            unverified += 1;
            used.add(rep);
            queue.push(rep);
            setSelected((prev) => {
              const next = new Set(prev);
              next.delete(id);
              next.add(rep);
              return next;
            });
            continue;
          }
          dead.add(id);
          setSelected((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          const rep = await takeReplacement(used, dead, controller.signal);
          if (rep) {
            used.add(rep);
            queue.push(rep);
            replaced += 1;
            setSelected((prev) => new Set(prev).add(rep));
          }
        }
        setVerifyState({
          checked,
          dead: dead.size,
          replaced,
          unverified,
          target: wanted,
        });
      }

      if (confirmed.length === 0) {
        setSubmitError(
          "Every picked listing has been taken down on the portal, and there was nothing left to replace them with. Widen the filters or scan deeper.",
        );
        return;
      }

      const r = await api<PublishResponse>("/api/competition-bulk/publish", {
        method: "POST",
        json: {
          hot_offer_ids: confirmed,
          agent_user_ids: orderedAgents,
          mh_pay_balance: mhPayBalance,
          use_ss: useSs,
        },
        signal: controller.signal,
      });
      const skipped = r.skipped_offer_ids?.length ?? 0;
      // The server re-checks its own records at publish time and drops rows
      // it knows are gone; fold those into the same "deleted" tally.
      for (const id of r.gone_offer_ids ?? []) dead.add(id);
      if (r.gone_offer_ids?.length) {
        setVerdicts((prev) => {
          const next = new Map(prev);
          for (const id of r.gone_offer_ids ?? []) next.set(id, "gone");
          return next;
        });
      }
      const agents = r.agent_offer_ids?.length ?? 0;
      const noPhoto = r.no_photo_offer_ids?.length ?? 0;
      if (r.jobs.length === 0) {
        setSubmitError(
          skipped > 0
            ? `All ${skipped} verified listing${skipped === 1 ? "" : "s"} are already published by the chosen agent${orderedAgents.length === 1 ? "" : "s"} — nothing to publish.`
            : agents > 0 && dead.size === 0
              ? `All ${agents} picked listing${agents === 1 ? "" : "s"} turned out to be posted by agencies registered as private sellers — nothing to publish.`
              : noPhoto > 0 && dead.size === 0 && agents === 0
                ? `All ${noPhoto} picked listing${noPhoto === 1 ? "" : "s"} have no photos, which MyHome requires — nothing to publish.`
                : dead.size > 0
                  ? "Every picked listing has been taken down on the portal or was posted by an agency — nothing to publish."
                  : "Nothing was published.",
        );
        return;
      }
      registerBatch({ batchId: r.batch_id, total: r.jobs.length, useSs });
      setResult(
        `Publishing ${r.jobs.length} listing${r.jobs.length === 1 ? "" : "s"} across ${orderedAgents.length} agent${orderedAgents.length === 1 ? "" : "s"}` +
          (dead.size
            ? ` · skipped ${dead.size} deleted listing${dead.size === 1 ? "" : "s"}${replaced ? `, replaced ${replaced}` : ""}`
            : "") +
          (unverified
            ? ` · swapped ${unverified} unverified listing${unverified === 1 ? "" : "s"}`
            : "") +
          (agents
            ? ` · skipped ${agents} agency listing${agents === 1 ? "" : "s"} (registered as private sellers)`
            : "") +
          (noPhoto
            ? ` · skipped ${noPhoto} listing${noPhoto === 1 ? "" : "s"} without photos`
            : "") +
          (skipped ? ` · ${skipped} already published` : ""),
      );
      // Published rows shouldn't stay armed for a second run.
      setSelected((prev) => {
        const next = new Set(prev);
        for (const id of confirmed) next.delete(id);
        return next;
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return;
      setSubmitError(
        e instanceof ApiError ? e.message : "Verification failed — try again",
      );
    } finally {
      setBusy(false);
      if (abortRef.current === controller) abortRef.current = null;
    }
  }

  // ── selection helpers ────────────────────────────────────────────
  function toggleListing(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  /** Re-pick from what's currently in view (score-ordered), so narrowing the
   *  list to e.g. "Vake, dedup only" and hitting this gives you the top N of
   *  exactly that. */
  function selectTopN() {
    const next = new Set<string>();
    for (const it of visibleItems) {
      if (next.size >= target) break;
      const cand = it.top_candidate?.listing;
      if (!cand || cand.placement_id) continue;
      if (verdicts.get(cand.id) === "gone") continue;
      next.add(cand.id);
    }
    setSelected(next);
  }
  function clearListings() {
    setSelected(new Set());
  }
  function toggleAgent(id: string) {
    setSelectedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function autoPickAgents() {
    if (!sortedTeam) return;
    const ok = sortedTeam.filter((m) => m.has_myhome_creds && m.has_myhome_phone);
    if (ok.length) setSelectedAgents(new Set(ok.map((m) => m.id)));
  }

  // Headline total across the picked firms. Prefers our exact rollup and
  // falls back to the portal's figure per firm, so the number appears
  // immediately and sharpens as the counts land.
  const pickedTotalListings = picked.reduce(
    (n, a) =>
      n +
      (pickedCounts.get(a.id)?.active_listings ??
        a.active_listings ??
        a.portal_listings ??
        0),
    0,
  );
  const pickedCountsPending = picked.some((a) => !pickedCounts.has(a.id));

  return (
    <div className="max-w-[1400px]">
      <PageHeader
        title="Competition bulk"
        subtitle="Pick a few competitors, find the owner behind each of their listings, verify the ad is still live, and publish."
      />

      {/* ─── STEP 1 · competitors ─── */}
      <section className="card p-4 mb-4">
        <StepHeading n={1} title="Competitors">
          {picked.length > 0 && (
            <span
              className="text-[11px] text-slate-400"
              title={
                pickedCountsPending
                  ? "Portal's own figure — resolving our exact count…"
                  : "Active listings we hold for these firms — what the scan will walk"
              }
            >
              {picked.length} picked · {pickedTotalListings.toLocaleString()}
              {pickedCountsPending ? "~" : ""} listings
            </span>
          )}
        </StepHeading>

        {picked.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {picked.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => togglePicked(a)}
                className="inline-flex items-center gap-1.5 rounded-full border border-accent-500/50 bg-accent-500/10 px-2.5 py-1 text-[11px] text-accent-100 hover:border-rose-500/50 hover:text-rose-200 transition"
                title="Remove from the scan"
              >
                <AgencyMark agency={a} />
                <span className="truncate max-w-[160px]">{a.name}</span>
                <AgencyCount
                  exact={pickedCounts.get(a.id)?.active_listings}
                  cached={a.active_listings}
                  portal={a.portal_listings}
                  className="tabular-nums text-slate-400"
                />
                <span className="text-slate-500">✕</span>
              </button>
            ))}
            <button
              type="button"
              className="text-[11px] text-slate-500 hover:text-slate-300 px-2"
              onClick={() => setPicked([])}
            >
              Clear
            </button>
          </div>
        )}

        <input
          className="input h-9 text-sm w-full sm:max-w-md"
          placeholder="Search a firm or broker by name, portal id, or profile URL…"
          value={agencyQuery}
          onChange={(e) => setAgencyQuery(e.target.value)}
        />

        {agencyError ? (
          <div className="text-rose-300 text-xs mt-3">{agencyError}</div>
        ) : agencyOptions === null ? (
          <div className="mt-3 space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-9" />
            ))}
          </div>
        ) : agencyOptions.length === 0 ? (
          <div className="text-xs text-slate-500 mt-3">
            No competitor matches that search.
          </div>
        ) : (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">
              {agencyQuery.trim()
                ? "Search results"
                : "Most active firms · last 30 days"}
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto -mr-2 pr-2">
              {agencyOptions.map((a) => {
                const on = picked.some((p) => p.id === a.id);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => togglePicked(a)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition ${
                      on
                        ? "border-accent-500/50 bg-accent-500/10 text-accent-200"
                        : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/20 hover:text-slate-100"
                    }`}
                    title={
                      (a.parent_name ? `Broker under ${a.parent_name} · ` : "") +
                      (a.active_listings != null
                        ? `${a.active_listings.toLocaleString()} active listings we hold`
                        : `${(a.portal_listings ?? 0).toLocaleString()} listings on the portal`) +
                      " — exact count resolves when you pick it"
                    }
                  >
                    <AgencyMark agency={a} />
                    <span className="truncate max-w-[160px]">{a.name}</span>
                    <AgencyCount
                      exact={pickedCounts.get(a.id)?.active_listings}
                      cached={a.active_listings}
                      portal={a.portal_listings}
                      className="text-[10px] tabular-nums px-1.5 py-0.5 rounded-full bg-white/[0.06] text-slate-300"
                    />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* ─── STEP 2 · scan ─── */}
      <section className="card p-4 mb-4">
        <StepHeading n={2} title="Scan their listings for owners">
          <span className="text-[11px] text-slate-500">
            newest posts first · runs until it has enough
          </span>
        </StepHeading>

        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-400">
            I want to publish
            <input
              type="number"
              min={1}
              max={500}
              value={target}
              disabled={scanning || busy}
              onChange={(e) => {
                const n = Number(e.target.value);
                setTarget(
                  Number.isFinite(n) && n > 0 ? Math.min(500, Math.floor(n)) : 1,
                );
              }}
              className="input !py-1 !text-xs h-9 w-20 text-right tabular-nums"
            />
            listings
          </label>

          <TimeRangeDropdown value={timeRange} onChange={setTimeRange} />

          <button
            type="button"
            className="btn-secondary text-sm"
            disabled={pickedIds.length === 0 || scanning || busy}
            onClick={startScan}
            title={
              pickedIds.length === 0
                ? "Pick at least one competitor first"
                : "Scan the selected competitors"
            }
          >
            {scanning ? "Scanning…" : items ? "↻ Re-scan" : "▶ Start scan"}
          </button>
          {scanning && (
            <button
              type="button"
              onClick={stopScan}
              className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/15 text-rose-200 text-sm px-3 h-9 transition"
            >
              ■ Stop
            </button>
          )}

          {items && (
            <ScanProgress
              scanning={scanning}
              collected={collected}
              want={target * RESERVE_MULTIPLIER}
              selected={selected.size}
              reserve={reserveCount}
              scanned={scanned}
              total={scanTotal}
              totalCapped={scanTotalCapped}
            />
          )}
        </div>

        <div className="mt-2">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-300 transition"
          >
            <Chevron open={filtersOpen} />
            Property filters · deal, price, m², rooms, district, condition —
            narrows what gets scanned
            {!filtersOpen && hasAnyFilter(filters) && (
              <span className="text-accent-300 normal-case tracking-normal">
                · active
              </span>
            )}
          </button>
          {filtersOpen && (
            <div className="mt-2">
              <FilterBar filters={filters} onChange={setFilters} hideOwnerDirect />
            </div>
          )}
        </div>

        {scanError && (
          <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2 mt-2">
            {scanError}
          </div>
        )}
      </section>

      {/* ─── STEP 3 · agents + publish ─── */}
      <section className="card p-4 mb-4">
        <StepHeading n={3} title="Verify + publish">
          <span className="text-[11px] text-slate-500">
            every listing is re-checked against the portal first
          </span>
        </StepHeading>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={autoPickAgents}
            disabled={(team?.length ?? 0) === 0}
            title="Auto-pick every credentialled active agent, lightest workload first"
          >
            ⚡ Auto-pick agents
          </button>
          <label className="text-xs text-slate-400 inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={strictCompetitor}
              onChange={(e) => setStrictCompetitor(e.target.checked)}
            />
            Skip if the competitor's ad is gone too
          </label>
          <label className="text-xs text-slate-400 inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={mhPayBalance}
              onChange={(e) => setMhPayBalance(e.target.checked)}
            />
            MyHome balance
          </label>
          <label className="text-xs text-slate-400 inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={useSs}
              onChange={(e) => setUseSs(e.target.checked)}
            />
            Also SS.ge
          </label>

          <div className="ml-auto flex items-center gap-2">
            {verifyState && (
              <span className="text-[11px] text-slate-400 tabular-nums">
                verified {verifyState.checked}/{verifyState.target}
                {verifyState.dead > 0 && (
                  <span className="text-rose-300">
                    {" "}
                    · {verifyState.dead} deleted
                  </span>
                )}
                {verifyState.replaced > 0 && (
                  <span className="text-emerald-300">
                    {" "}
                    · {verifyState.replaced} replaced
                  </span>
                )}
                {verifyState.unverified > 0 && (
                  <span className="text-amber-300">
                    {" "}
                    · {verifyState.unverified} unverified, swapped
                  </span>
                )}
              </span>
            )}
            <button
              className="btn-primary text-sm"
              disabled={!canPublish}
              onClick={verifyAndPublish}
              title={
                !canPublish
                  ? "Pick at least one listing and one agent"
                  : `Check ${orderedSelected.length} listings are still live, then publish across ${orderedAgents.length} agents`
              }
            >
              {busy
                ? "Verifying…"
                : orderedSelected.length > 0 && orderedAgents.length > 0
                  ? `Verify & publish ${orderedSelected.length} → ${orderedAgents.length}`
                  : "Verify & publish"}
            </button>
          </div>
        </div>

        {teamError ? (
          <div className="text-rose-300 text-xs">{teamError}</div>
        ) : sortedTeam === null ? (
          <Skeleton className="h-10" />
        ) : sortedTeam.length === 0 ? (
          <div className="text-xs text-slate-500">No active team members.</div>
        ) : (
          <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto -mr-2 pr-2">
            {sortedTeam.map((m) => {
              const on = selectedAgents.has(m.id);
              const credsOk = m.has_myhome_creds && m.has_myhome_phone;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggleAgent(m.id)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition ${
                    !credsOk
                      ? on
                        ? "border-rose-500/50 bg-rose-500/10 text-rose-200"
                        : "border-rose-500/30 bg-rose-500/[0.04] text-rose-300/80 hover:border-rose-500/50"
                      : on
                        ? "border-accent-500/50 bg-accent-500/10 text-accent-200"
                        : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/20 hover:text-slate-100"
                  }`}
                >
                  <Avatar
                    url={m.avatar_url}
                    name={m.full_name}
                    email={m.email}
                    size="xs"
                    online={m.is_online}
                  />
                  <span className="truncate max-w-[130px]">
                    {m.full_name || m.email}
                  </span>
                  <span className="text-[10px] tabular-nums px-1.5 py-0.5 rounded-full bg-white/[0.06] text-slate-300">
                    {m.placements_today ?? 0}
                  </span>
                  {!credsOk && (
                    <span
                      className="text-[10px] text-rose-300"
                      title="MyHome credentials missing"
                    >
                      ⛔
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {uncredentialled.length > 0 && (
          <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2 mt-2">
            ⛔{" "}
            {uncredentialled
              .map((m) => m.full_name?.split(" ")[0] || m.email)
              .join(", ")}{" "}
            {uncredentialled.length === 1 ? "is" : "are"} missing MyHome creds —
            unpick {uncredentialled.length === 1 ? "them" : "those"} to publish.
          </div>
        )}
        {submitError && (
          <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2 mt-2">
            {submitError}
          </div>
        )}
        {result && (
          <div className="text-emerald-200 text-xs bg-emerald-500/10 border border-emerald-500/25 rounded p-2 mt-2">
            ✓ {result}
          </div>
        )}
      </section>

      {/* ─── matches ─── */}
      <section className="card p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="label !mb-0 text-xs">
            Matches{" "}
            <span className="text-slate-600 font-normal">
              · their listing → the owner's listing
            </span>
            {selected.size > 0 && (
              <span className="ml-2 text-slate-300 font-normal">
                · {selected.size} picked
              </span>
            )}
            {rowFiltersActive && (
              <span className="ml-2 text-accent-300 font-normal">
                · showing {visibleItems.length.toLocaleString()} of{" "}
                {displayItems.length.toLocaleString()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              className="text-[11px] rounded-md border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-slate-300 px-2 py-1 disabled:opacity-40"
              onClick={selectTopN}
              disabled={matchedCount === 0}
              title={
                rowFiltersActive
                  ? `Pick the ${target} strongest unpublished matches from the filtered view`
                  : `Pick the ${target} strongest unpublished matches`
              }
            >
              Pick top {target}
              {rowFiltersActive ? " in view" : ""}
            </button>
            <button
              type="button"
              className="text-[11px] rounded-md border border-white/10 bg-white/[0.03] hover:bg-rose-500/10 hover:border-rose-500/30 hover:text-rose-200 text-slate-300 px-2 py-1 disabled:opacity-40"
              onClick={clearListings}
              disabled={selected.size === 0}
            >
              Clear
            </button>
          </div>
        </div>

        {/* ── result filters ──
            Narrow what's shown without re-scanning. Selection survives
            filtering, so you can pick in one view and keep picking in
            another; the counters above always describe the full result set. */}
        {items && displayItems.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 mb-3 pb-3 border-b border-white/5">
            <div className="inline-flex rounded-lg bg-ink-900/60 border border-white/5 p-0.5 text-[11px] font-medium">
              {VIEW_LABELS.map((v) => (
                <button
                  key={v.value}
                  type="button"
                  onClick={() => setView(v.value)}
                  className={`px-2.5 py-1 rounded-md transition-colors ${
                    view === v.value
                      ? "bg-accent-500/15 text-accent-200"
                      : "text-slate-500 hover:text-slate-300"
                  }`}
                >
                  {v.label}
                  <span className="ml-1 tabular-nums opacity-70">
                    {viewCounts[v.value].toLocaleString()}
                  </span>
                </button>
              ))}
            </div>

            {picked.length > 1 && (
              <select
                className="input !py-1 !text-xs h-8 w-auto"
                value={agencyFilter}
                onChange={(e) => setAgencyFilter(e.target.value)}
                title="Show only matches found through one of the picked competitors"
              >
                <option value="">All competitors</option>
                {picked.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            )}

            <select
              className="input !py-1 !text-xs h-8 w-auto"
              value={methodFilter}
              onChange={(e) => setMethodFilter(e.target.value)}
              title="How the owner listing was matched"
            >
              {METHOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>

            <select
              className="input !py-1 !text-xs h-8 w-auto"
              value={minScore}
              onChange={(e) => setMinScore(Number(e.target.value))}
              title="Minimum match confidence"
            >
              {SCORE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>

            <input
              className="input !py-1 !text-xs h-8 w-48"
              placeholder="Title, street, area, id…"
              value={rowQuery}
              onChange={(e) => setRowQuery(e.target.value)}
            />

            {rowFiltersActive && (
              <button
                type="button"
                className="text-[11px] text-slate-500 hover:text-slate-200 px-2"
                onClick={clearRowFilters}
              >
                Reset filters
              </button>
            )}
          </div>
        )}

        {!items ? (
          <div className="card p-8 text-center text-sm text-slate-500 border-dashed">
            <div className="text-slate-300 font-medium mb-1">
              Pick competitors, then start the scan
            </div>
            <div className="text-xs text-slate-500">
              Starting from their newest posts, each listing is matched against
              our data for the same property posted by its actual owner. The
              scan runs until it has your {target} plus a reserve — if one turns
              out deleted during verification, the next reserve match takes its
              place, and if the reserve runs out it keeps digging on its own.
            </div>
          </div>
        ) : displayItems.length === 0 && scanning ? (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        ) : displayItems.length === 0 ? (
          <div className="card p-8 text-center text-sm text-slate-500">
            Nothing publishable found for these competitors in this window.
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="card p-8 text-center text-sm text-slate-500">
            No match in this view.{" "}
            <button
              type="button"
              className="text-accent-300 hover:underline"
              onClick={clearRowFilters}
            >
              Reset the filters
            </button>{" "}
            to see all {displayItems.length.toLocaleString()} rows.
          </div>
        ) : (
          <div className="space-y-1.5">
            {visibleItems.map((it) => {
              const cid = it.top_candidate?.listing.id;
              return (
                <MatchRow
                  key={it.competitor_listing.id}
                  item={it}
                  selected={!!cid && selected.has(cid)}
                  ownerVerdict={cid ? verdicts.get(cid) : undefined}
                  competitorVerdict={verdicts.get(it.competitor_listing.id)}
                  onToggle={() => cid && toggleListing(cid)}
                />
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

/** Fold a freshly-fetched page into the verify loop's live view. */
function indexPage(
  view: { items: FinderItem[]; byCandidate: Map<string, FinderItem> },
  page: FinderItem[],
): void {
  for (const it of page) {
    const cid = it.top_candidate?.listing.id;
    if (cid && !view.byCandidate.has(cid)) view.byCandidate.set(cid, it);
  }
  view.items = view.items.concat(page);
}

function StepHeading({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="w-5 h-5 rounded-full bg-accent-500/15 border border-accent-500/30 text-accent-200 text-[11px] font-semibold inline-flex items-center justify-center">
        {n}
      </span>
      <span className="text-sm font-medium text-slate-200">{title}</span>
      <span className="ml-auto">{children}</span>
    </div>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="9"
      height="9"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`transition-transform ${open ? "rotate-90" : ""}`}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/** Listing count on an agency chip, in descending order of trust: the live
 *  rollup once it lands, else our nightly cached rollup, else the portal's own
 *  figure. Both fallbacks are marked "~" so nobody reads an approximation as
 *  the number the scan will walk. */
function AgencyCount({
  exact,
  cached,
  portal,
  className,
}: {
  exact?: number;
  cached?: number | null;
  portal: number | null;
  className?: string;
}) {
  if (exact !== undefined) {
    return (
      <span className={className} title="Active listings we hold for this firm">
        {exact.toLocaleString()}
      </span>
    );
  }
  if (cached != null) {
    return (
      <span
        className={className}
        title="Active listings we hold for this firm (cached — exact count resolves when you pick it)"
      >
        ~{cached.toLocaleString()}
      </span>
    );
  }
  if (portal == null) return null;
  return (
    <span className={className} title="The portal's own listing count">
      ~{portal.toLocaleString()}
    </span>
  );
}

function AgencyMark({ agency }: { agency: AgencyRow }) {
  return agency.logo_url ? (
    <img
      src={agency.logo_url}
      alt=""
      className="w-4 h-4 rounded object-cover border border-white/10 bg-white/5 shrink-0"
      loading="lazy"
    />
  ) : (
    <span
      className="w-2.5 h-2.5 rounded-full shrink-0"
      style={{ background: agency.color_hex || "#64748b" }}
    />
  );
}

/** Progress is measured in *found inventory*, not rows walked — the user
 *  asked for N listings, so the bar fills toward N + its reserve. Rows walked
 *  is reported alongside as context ("we're 4,000 listings deep into their
 *  38,000"), not as the goal. */
function ScanProgress({
  scanning,
  collected,
  want,
  selected,
  reserve,
  scanned,
  total,
  totalCapped,
}: {
  scanning: boolean;
  collected: number;
  want: number;
  selected: number;
  reserve: number;
  scanned: number;
  total: number;
  totalCapped: boolean;
}) {
  const percent = Math.min(100, (collected / Math.max(1, want)) * 100);
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-[11px] text-slate-500 whitespace-nowrap inline-flex items-center gap-1.5">
        {scanning && (
          <span
            className="inline-block w-2 h-2 rounded-full bg-accent-400 animate-pulse"
            aria-hidden
          />
        )}
        <span className="text-accent-200 font-medium tabular-nums">
          {selected.toLocaleString()}
        </span>{" "}
        picked ·{" "}
        <span
          className="text-slate-300 tabular-nums"
          title="Spare matches held back to replace listings that turn out deleted"
        >
          {reserve.toLocaleString()}
        </span>{" "}
        in reserve · walked{" "}
        <span className="text-slate-300 tabular-nums">
          {scanned.toLocaleString()}
        </span>
        {total > 0 && (
          <>
            {" "}
            of{" "}
            <span
              className="text-slate-500 tabular-nums"
              title={
                totalCapped
                  ? "They have at least this many — we stop counting here so a full count of a 12 GB table can't stall the scan"
                  : undefined
              }
            >
              {total.toLocaleString()}
              {totalCapped ? "+" : ""}
            </span>{" "}
            of their listings
          </>
        )}
      </span>
      <div className="relative h-1.5 w-24 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-accent-500 to-accent-400 transition-[width] duration-300"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

function MatchRow({
  item,
  selected,
  ownerVerdict,
  competitorVerdict,
  onToggle,
}: {
  item: FinderItem;
  selected: boolean;
  ownerVerdict?: Verdict;
  competitorVerdict?: Verdict;
  onToggle: () => void;
}) {
  const c = item.competitor_listing;
  const m = item.top_candidate?.listing;
  const score = item.top_candidate?.score ?? 0;
  const method = item.top_candidate?.method;
  const alreadyPlaced = m?.placement_id ?? null;
  const placedBy = m?.placed_by_user ?? null;
  const noCandidate = item.top_candidate === null;
  const gone = ownerVerdict === "gone" || competitorVerdict === "gone";

  return (
    <label
      className={`flex items-center gap-3 rounded-lg border px-3 py-2 transition cursor-pointer ${
        noCandidate
          ? "border-white/5 bg-white/[0.02] cursor-not-allowed opacity-60"
          : gone
            ? "border-rose-500/30 bg-rose-500/[0.05] opacity-70"
            : alreadyPlaced
              ? "border-amber-500/30 bg-amber-500/[0.06]"
              : selected
                ? "border-accent-500/50 bg-accent-500/[0.06]"
                : "border-white/8 bg-white/[0.02] hover:border-white/15"
      }`}
    >
      <input
        type="checkbox"
        className="w-4 h-4 accent-accent-500"
        checked={selected}
        disabled={noCandidate}
        onChange={onToggle}
      />
      <div
        className="w-[92px] shrink-0 flex items-center gap-1.5 min-w-0"
        title={c.market_agency_name || "Unknown agency"}
      >
        {c.market_agency_logo_url ? (
          <img
            src={c.market_agency_logo_url}
            alt=""
            className="w-5 h-5 rounded object-cover border border-white/10 shrink-0 bg-white/5"
            loading="lazy"
          />
        ) : (
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ background: c.market_agency_color_hex || "#64748b" }}
          />
        )}
        <span className="text-[10px] text-slate-400 truncate">
          {c.market_agency_name || "—"}
        </span>
      </div>
      <ListingThumb listing={c} muted verdict={competitorVerdict} />
      <div className="text-slate-600 px-1">→</div>
      {m ? (
        <ListingThumb listing={m} verdict={ownerVerdict} />
      ) : (
        <div className="flex-1 text-xs text-slate-500 italic">
          No owner-direct match in our data
        </div>
      )}
      {m && (
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {alreadyPlaced && (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-amber-200 bg-amber-500/15 border border-amber-500/30 rounded px-1.5 py-0.5"
              title={
                placedBy
                  ? `Already uploaded by ${placedBy.full_name || placedBy.email}`
                  : "Already uploaded by your agency"
              }
            >
              {placedBy && (
                <Avatar
                  url={placedBy.avatar_url}
                  name={placedBy.full_name}
                  email={placedBy.email}
                  size="xs"
                />
              )}
              <span>Already uploaded</span>
            </span>
          )}
          <span
            className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 ${
              method && CERTAIN_METHODS.has(method)
                ? "text-emerald-300 bg-emerald-500/15 border border-emerald-500/30"
                : "text-slate-300 bg-white/[0.06] border border-white/10"
            }`}
            title={item.top_candidate?.reasons.join(" · ")}
          >
            {(method && METHOD_LABELS[method]) || method}
          </span>
          <span className="text-[11px] tabular-nums font-semibold text-slate-200">
            {score}
          </span>
        </div>
      )}
    </label>
  );
}

function ListingThumb({
  listing,
  muted = false,
  verdict,
}: {
  listing: MarketListing;
  muted?: boolean;
  verdict?: Verdict;
}) {
  const price = listing.price_usd
    ? `$${Math.round(Number(listing.price_usd)).toLocaleString()}`
    : listing.price
      ? `${listing.price} ${listing.currency ?? ""}`
      : "—";
  const area = listing.area_sqm
    ? `${Math.round(Number(listing.area_sqm))} m²`
    : null;
  const where = listing.neighborhood || listing.district || "";
  return (
    <div className="flex items-center gap-2 min-w-0 flex-1">
      {listing.cover_photo_url ? (
        <img
          src={listing.cover_photo_url}
          alt=""
          className="w-12 h-12 rounded object-cover border border-white/5 shrink-0"
          loading="lazy"
        />
      ) : (
        <div className="w-12 h-12 rounded bg-white/[0.04] border border-white/5 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <div
          className={`text-[12px] truncate flex items-center gap-1.5 ${muted ? "text-slate-400" : "text-slate-100"}`}
        >
          <a
            href={listing.published_url}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline truncate"
            onClick={(e) => e.stopPropagation()}
          >
            {listing.title || `${listing.platform} · ${listing.external_id}`}
          </a>
          <VerdictChip verdict={verdict} />
        </div>
        <div className="text-[11px] text-slate-500 truncate">
          {listing.platform.toUpperCase()} · {price}
          {area ? ` · ${area}` : ""}
          {listing.rooms ? ` · ${listing.rooms} rooms` : ""}
          {where ? ` · ${where}` : ""}
        </div>
      </div>
    </div>
  );
}

/** Outcome of the portal probe, once one has run for this listing. */
function VerdictChip({ verdict }: { verdict?: Verdict }) {
  if (!verdict) return null;
  if (verdict === "live")
    return (
      <span
        className="text-[10px] text-emerald-300 shrink-0"
        title="Confirmed still on the portal"
      >
        ✓ live
      </span>
    );
  if (verdict === "gone")
    return (
      <span
        className="text-[10px] text-rose-300 shrink-0"
        title="The portal says this listing no longer exists — skipped"
      >
        ✕ deleted
      </span>
    );
  return (
    <span
      className="text-[10px] text-amber-300/80 shrink-0"
      title="The portal didn't answer — kept, since a blip shouldn't drop real inventory"
    >
      ? unverified
    </span>
  );
}
